import React, { useCallback, useMemo, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import sh from "bundles/common/utils/sh";
import CandidateCardNew from "bundles/common/components/CandidateCardNew";
import CandidateDialog from "bundles/common/components/CandidateDialog";

const ClosedCandidatesSection = ({ applications = [], query = "", onApplicationReopened, onKanbanMetaUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [candidatesLimit, setCandidatesLimit] = useState(6);
  const [dialogJobApplication, setDialogJobApplication] = useState(null);
  const [dialogCandidate, setDialogCandidate] = useState(null);

  const filtered = useMemo(() => {
    const raw = Array.isArray(applications) ? applications : [];
    const q = (query || "").trim().toLowerCase();
    if (!q) return raw;

    return raw.filter((c) => {
      const name = (c?.candidate?.full_name || "").toLowerCase();
      const offer = (c?.job_offer?.job_title || "").toLowerCase();
      return name.includes(q) || offer.includes(q);
    });
  }, [applications, query]);

  const handleToggleFavorite = useCallback(
    (candidateId) => {
      const prev = currentRecruiter?.favorite_candidate_ids || [];
      const isAlreadySaved = prev.some((id) => String(id) === String(candidateId));
      const candidateIdNum = Number(candidateId);
      const next = isAlreadySaved
        ? prev.filter((id) => String(id) !== String(candidateId))
        : [...prev, Number.isFinite(candidateIdNum) ? candidateIdNum : candidateId];

      dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: { ...(currentRecruiter || {}), favorite_candidate_ids: next } });

      sh.put("/update_favorite_candidates", {
        authenticity_token: authenticityToken,
        favorite_candidates: next.map((id) => String(id)),
      })
        .then((res) => {
          if (res?.data) dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: res.data });
        })
        .catch((e) => {
          console.error(e);
          dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: { ...(currentRecruiter || {}), favorite_candidate_ids: prev } });
          enqueueSnackbar(intl.formatMessage({ id: "recruiter.favorite_update_failed" }), { variant: "error" });
        });
    },
    [authenticityToken, currentRecruiter, dispatch, enqueueSnackbar, intl]
  );

  const openDialogForCard = useCallback((jobApplication, candidate) => {
    setDialogJobApplication(jobApplication);
    setDialogCandidate(candidate);
    setIsDialogOpen(true);
  }, []);

  if (!filtered.length) return null;

  return (
    <Box sx={{ mt: 4, padding: "12px", border: `1px solid ${theme.palette.background.neutral}`, borderRadius: "12px" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "10px" }}>
        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "16px !important", color: theme.palette.secondary.black }}>
          <FormattedMessage id="recruiter.kanban.closed_applications" defaultMessage="Closed" />
        </Typography>
        <Box sx={{ padding: "4px", backgroundColor: theme.palette.secondary.box, borderRadius: "4px", color: theme.palette.text.main }}>
          <Typography sx={{ fontWeight: 600 }}>
            {filtered.length}
          </Typography>
        </Box>
      </Box>

      {isMobile ? (
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateRows: "repeat(2, auto)",
            gridAutoColumns: "minmax(350px, 85vw)",
            gap: 1.5,
            overflowX: "auto",
            overflowY: "hidden",
            pb: 1,
          }}
        >
          {filtered.slice(0, candidatesLimit).map((card) => {
            const candidateForCard = {
              ...(card?.candidate || {}),
              profile_picture_url: card?.candidate?.profile_picture_url,
              sent_from_talent_team: card?.sent_from_talent_team,
            };

            return (
              <Box key={`closed-${card.id}`} sx={{ minWidth: 0 }}>
                <CandidateCardNew
                  candidate={candidateForCard}
                  showCloseIcon={false}
                  pinSaveIconToBottom={true}
                  showMetaChips={false}
                  favorites={currentRecruiter?.favorite_candidate_ids || []}
                  handleFavoriteToggle={(candidateId, action) => {
                    if (action !== "toggle") return;
                    if (!candidateId) return;
                    handleToggleFavorite(candidateId);
                  }}
                  onActionButtonClick={() => openDialogForCard(card, candidateForCard)}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {filtered.slice(0, candidatesLimit).map((card) => {
            const candidateForCard = {
              ...(card?.candidate || {}),
              profile_picture_url: card?.candidate?.profile_picture_url,
              sent_from_talent_team: card?.sent_from_talent_team,
            };

            return (
              <Grid item xs={12} md={4} key={`closed-${card.id}`} sx={{ display: "flex" }}>
                <CandidateCardNew
                  candidate={candidateForCard}
                  showCloseIcon={false}
                  pinSaveIconToBottom={true}
                  showMetaChips={false}
                  favorites={currentRecruiter?.favorite_candidate_ids || []}
                  handleFavoriteToggle={(candidateId, action) => {
                    if (action !== "toggle") return;
                    if (!candidateId) return;
                    handleToggleFavorite(candidateId);
                  }}
                  onActionButtonClick={() => openDialogForCard(card, candidateForCard)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {filtered.length > candidatesLimit ? (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setCandidatesLimit((prev) => Math.min(prev + 6, filtered.length))}

            sx={{
              borderRadius: "999px",
              padding: "12px 24px",
              textTransform: "none",
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: "transparent",
                borderColor: theme.palette.text.primary,
              },
            }}
          >
            <FormattedMessage id="recruiter.kanban.see_more" defaultMessage="View more" />
          </Button>
        </Box>
      ) : null}


      {dialogJobApplication && dialogCandidate ? (
        <CandidateDialog
          candidate={dialogCandidate}
          jobApplication={dialogJobApplication}
          jobApplications={dialogJobApplication?.job_applications || [dialogJobApplication]}
          onKanbanMetaUpdate={onKanbanMetaUpdate}
          onJobApplicationReopened={(reopenedApp) => {
            if (typeof onApplicationReopened === "function") {
              const updatedJobApplications = (dialogJobApplication?.job_applications || [dialogJobApplication]).map((app) =>
                app.id === reopenedApp?.id ? { ...app, status: "pending" } : app
              );
              
              onApplicationReopened({
                id: dialogJobApplication?.id,
                candidate_id: dialogCandidate?.id,
                candidate: dialogCandidate,
                kanban_column_id: dialogJobApplication?.kanban_column_id,
                position: dialogJobApplication?.position,
                job_applications: updatedJobApplications,
              });
            }
            setIsDialogOpen(false);
            setDialogJobApplication(null);
            setDialogCandidate(null);
          }}
          onClose={() => {
            setIsDialogOpen(false);
            setDialogJobApplication(null);
            setDialogCandidate(null);
          }}
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          columns={[]}
          currentColumnId={null}
          onMoveToColumn={null}
          isModal={true}
        />
      ) : null}
    </Box>
  );
};

export default ClosedCandidatesSection;
